import { Navigate, useLocation } from "react-router-dom";
import { isRoyalTeamDepartment, type EditablePermissionKey } from "@mtform/shared";
import { useAuth } from "@/auth/AuthContext";
import { PageLoader } from "@/components/ui/page-loader";

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** Restrict access to specific roles. Default: any authenticated user. */
  allow?: Array<"super_admin" | "admin" | "user">;
  /** When true, additionally restrict to members of the Royal /
   *  TMS operations team (by department). Used for the TMS Cleanup
   *  queue, which Mount Sinai admins shouldn't be shown. */
  royalTeamOnly?: boolean;
  /** Gate on a configurable permission (the role-permissions matrix)
   *  instead of (or in addition to) a fixed role list. Super-admins
   *  always pass. Used by /booked, whose access is toggleable. */
  permission?: EditablePermissionKey;
}

// =============================================================================
// ProtectedRoute
//
// Wraps any route that requires authentication. While the initial /me is
// in flight, render the branded PageLoader — better than briefly
// flashing the login screen on a returning session. (The loader keeps
// itself invisible for its first 150ms, so a near-instant /me never
// flashes it.) Once we know the answer:
//   - unauthenticated → redirect to /login (preserving the intended URL
//     so login can bounce back).
//   - authenticated but role mismatch → redirect to /, the user has no
//     business on this page.
// =============================================================================

export function ProtectedRoute({
  children,
  allow,
  royalTeamOnly,
  permission,
}: ProtectedRouteProps) {
  const { state, sessionExpired } = useAuth();
  const location = useLocation();

  if (state.status === "loading") {
    return <PageLoader label="Checking your session" />;
  }

  if (state.status === "unauthenticated") {
    return (
      <Navigate
        to="/login"
        replace
        state={{
          from: `${location.pathname}${location.search}`,
          // Distinguishes "your session expired" (kicked out mid-use) from a
          // plain "you need to sign in" so the login page can say so.
          reason: sessionExpired ? "expired" : undefined,
        }}
      />
    );
  }

  if (allow && !allow.includes(state.user.role)) {
    return <Navigate to="/" replace />;
  }

  // Permission gate (configurable). Super-admins always pass; others
  // need the flag in their effective-permissions map.
  if (permission) {
    const allowed =
      state.user.role === "super_admin" ||
      Boolean(state.user.permissions?.[permission]);
    if (!allowed) return <Navigate to="/" replace />;
  }

  // Department-scoped section (TMS Cleanup). A Mount Sinai admin who
  // navigates here directly gets bounced to the admin home rather than
  // shown an empty / forbidden page.
  if (royalTeamOnly && !isRoyalTeamDepartment(state.user.department)) {
    return <Navigate to="/admin/users" replace />;
  }

  return <>{children}</>;
}
